import { addDays } from "date-fns";
import { useState } from "react";

import type { CommunityPostEventTypeDto, ImageDto, MessageCreateRequest } from "@/api/types";
import { CommunityPostGallery } from "@/components/CommunityFeed/CommunityPostMedia/CommunityPostGallery";
import { CommunityPostVideoForAppPreview } from "@/components/CommunityFeed/CommunityPostMedia/CommunityPostVideoForAppPreview";
import type { FormDocument } from "@/components/DocumentInput/useDocumentInput";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { AlertTriangleIcon } from "@/components/Icons/AlertTriangleIcon";
import { Announcement03Icon } from "@/components/Icons/Announcement03Icon";
import { Phone01Icon } from "@/components/Icons/Phone01Icon";
import { Share06Icon } from "@/components/Icons/Share06Icon";
import { ThumbsUpIcon } from "@/components/Icons/ThumbsUpIcon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { DocumentMedia } from "@/components/Media/DocumentMedia";
import { Modal } from "@/components/Modal/Modal";
import { Phone } from "@/components/Phone/Phone";
import { PhoneInner } from "@/components/Phone/PhoneInner";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import type { FormVideo, InputFile } from "@/components/VideoInput/useVideoInput";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { ContentBlock } from "@/design-system/ContentBlock";
import { Inline } from "@/design-system/Inline";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { unfurlDeepLinkFromContent } from "@/helpers/deep-link";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

import { CountdownBadge } from "../Badge/CountdownBadge";
import { DatedAnnouncementDetails } from "../EntityPreview/DatedAnnouncementDetails";
import { EntityPreview } from "../EntityPreview/EntityPreview";
import { EventPreviewCard } from "../EntityPreview/EventPreviewCard";
import { CheckDone02Icon } from "../Icons/CheckDone02Icon";
import { MessageCircle01Icon } from "../Icons/MessageCircle01Icon";
import { MultiLineContent } from "../MultiLineContent/MultiLineContent";
import type { PollData } from "../Poll/EditPollModal";

interface CreatePostAppPreviewProps {
  title: string;
  content: string;
  images: FormImage[];
  videos: FormVideo[];
  poll?: PollData;
  documents: FormDocument[];
  groupName: string | undefined;
  postType: MessageCreateRequest["type"] | undefined;
  /** Used for an event card when neither its preview URL nor its event image is available. */
  fallbackBackgroundImage?: ImageDto;
  event?: {
    type: CommunityPostEventTypeDto | undefined;
    startsAt: Date | undefined;
    endsAt: Date | undefined;
    location?: string;
  };
}
export function CreatePostAppPreview({
  title,
  content: rawContent,
  images,
  videos,
  documents,
  poll,
  groupName,
  postType,
  event,
  fallbackBackgroundImage,
}: CreatePostAppPreviewProps): React.ReactNode {
  const [previewDate] = useState(() => new Date());
  const [isModalOpened, modalHandler] = useBool(false);
  const { t } = useTranslation();

  const content = rawContent.trim();

  const getLineCount = () => {
    // 30 characters per line for title
    const wrappedTitle = wrapText(title, 30);
    // 50 characters per line for content
    const wrappedContent = wrapText(content, 50);

    return wrappedTitle.length + wrappedContent.length;
  };

  const { content: displayedContent, entity: attachedEntity } = unfurlDeepLinkFromContent(content);
  const eventImage = images[0];
  const isPostLengthWarningVisible = getLineCount() > 12;
  const previewSection = (
    <section className="mx-auto flex w-full max-w-compact-preview flex-col">
      <Phone>
        <PhoneInner noBackground>
          <section className="relative flex min-h-full flex-1 flex-col items-center overflow-hidden bg-aop-off-white pb-space-md">
            <span className="flex h-phone-preview-header w-full shrink-0 items-end justify-center border-b border-border-secondary bg-aop-off-white pb-space-sm text-center text-caption-emphasis text-text-brand-secondary">
              {t("component.create-post-app-preview.community")}
            </span>
            <article className="flex w-full flex-col gap-space-md overflow-y-auto bg-background-default p-space-md pb-0 shadow-raised">
              {(postType === "announcement" || postType === "announcementReadOnly") && (
                <div className="-mb-space-sm origin-top-left scale-75">
                  <Badge variant="special" icon={<Announcement03Icon />}>
                    {t("component.create-post-app-preview.announcement")}
                  </Badge>
                </div>
              )}
              <header className="flex grow items-center gap-space-sm">
                <UserAvatar image={undefined} />
                <Stack gap="xs">
                  <span
                    // oxlint-disable-next-line tailwindcss/no-conflicting-classes -- TODO: `flex` cancels line-clamp-1 (both set display)
                    className="line-clamp-1 flex items-center"
                  >
                    <span className="text-overline-emphasis text-text-primary">
                      {t("component.create-post-app-preview.username")}
                      {groupName && (
                        <span className="text-overline-emphasis text-text-brand-secondary">
                          {` ${t("component.community-post.posted-in-group", {
                            group: groupName,
                          })}`}
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="line-clamp-1 text-overline text-text-secondary">
                    <FormattedDate format="datetime" date={previewDate} />
                  </span>
                </Stack>
              </header>
              <Stack gap="sm">
                {title && <span className="text-caption-emphasis">{title}</span>}
                {displayedContent && (
                  <span className="w-full text-caption">
                    <MultiLineContent shouldHighlightLinks>{displayedContent}</MultiLineContent>
                  </span>
                )}
                {content && (
                  <span className="text-label-xs text-text-brand">
                    {t("component.create-post-app-preview.translate")}
                  </span>
                )}
              </Stack>
              {images && images.length > 0 && (
                <figure className="flex w-full justify-center overflow-hidden rounded-lg [&>div]:overflow-auto [&>div]:rounded-none">
                  <CommunityPostGallery
                    {...{
                      images,
                    }}
                  />
                </figure>
              )}
              {videos && videos.length > 0 && <CommunityPostVideoForAppPreview video={videos[0] as InputFile} />}
              {attachedEntity && (
                <ContentBlock width="flow" align="left">
                  <EntityPreview
                    entityId={attachedEntity.payload.entityId}
                    entityType={attachedEntity.payload.entityType}
                    otherIds={{
                      folderId: attachedEntity.payload.folderId,
                    }}
                    readOnly
                    shouldLoad
                  />
                </ContentBlock>
              )}
              {documents && documents.length > 0 && (
                <Inline gap="sm">
                  {documents.map((document, idx) => (
                    <DocumentMedia
                      key={idx}
                      {...{
                        document,
                      }}
                    />
                  ))}
                </Inline>
              )}
              {event?.startsAt && (
                <section data-testid="post-event">
                  {event.type === "common" ? (
                    <EventPreviewCard
                      title={title}
                      startsAt={event.startsAt}
                      endsAt={event.endsAt}
                      location={event.location}
                      image={eventImage && "id" in eventImage ? eventImage : undefined}
                      fallbackImage={fallbackBackgroundImage}
                      imageUrl={eventImage && "file" in eventImage ? eventImage.url : undefined}
                    />
                  ) : (
                    <DatedAnnouncementDetails
                      size="sm"
                      startsAt={event.startsAt}
                      endsAt={event.endsAt}
                      location={event.location}
                    />
                  )}
                </section>
              )}
              {poll && (
                <section className="flex flex-col gap-space-sm rounded-sm bg-background-default p-space-xs py-space-sm shadow-surface">
                  <div className="-mb-space-sm origin-top-left scale-75">
                    <CountdownBadge endDate={addDays(previewDate, poll.durationInDays)} />
                  </div>
                  {poll.question && <span className="text-caption-emphasis">{poll.question}</span>}
                  {poll.allowMultipleAnswers && (
                    <span className="flex w-full items-center gap-0.5 text-label-xs text-text-tertiary">
                      <CheckDone02Icon size={12} />
                      <span className="text-label-xs">{t("component.community-poll.form.label")}</span>
                    </span>
                  )}
                  <ul className="flex flex-col gap-0.5">
                    {poll.options.map((option, idx) => (
                      <li
                        key={idx}
                        className="flex items-center gap-space-sm rounded-sm border border-border-default bg-background-default px-space-sm py-card-compact"
                      >
                        <span className="size-status-dot rounded-full border border-border-default" />
                        <span className="text-label-xs">{option}</span>
                      </li>
                    ))}
                  </ul>
                  <span className="flex items-center justify-center rounded-xs bg-background-brand py-space-xs text-overline-emphasis text-text-on-brand">
                    {t("component.community-poll.button.vote")}
                  </span>
                  <span className="flex h-icon-inline items-center justify-center text-label-xs text-text-brand">
                    {t("component.community-poll.button.view-results")}
                  </span>
                </section>
              )}

              <footer className="flex w-full items-center justify-between py-space-sm text-overline text-text-secondary">
                <Inline gap="sm" align="center">
                  <span className="flex items-center gap-0.5">
                    <ThumbsUpIcon />
                    <span>{t("component.create-post-app-preview.like")}</span>
                  </span>
                  {(postType === "announcement" || postType === "undefined") && (
                    <span className="flex items-center gap-0.5">
                      <MessageCircle01Icon />
                      <span>{t("component.create-post-app-preview.comment")}</span>
                    </span>
                  )}
                </Inline>
                <Share06Icon />
              </footer>
            </article>
          </section>
        </PhoneInner>
      </Phone>
    </section>
  );

  return (
    <>
      {/* Desktop */}
      <section className="hidden @md:block">
        <section className="flex size-full flex-col gap-space-md">
          {previewSection}
          {isPostLengthWarningVisible && (
            <Notice
              variant="warning"
              icon={<AlertTriangleIcon />}
              message={t("component.create-post-app-preview.warning")}
            />
          )}
        </section>
      </section>
      {/* Mobile */}
      <aside className="w-fit @md:hidden">
        <Button variant="subtle" icon={<Phone01Icon />} onClick={modalHandler.setTrue}>
          {t("page.admin-create-post.section.preview")}
        </Button>
      </aside>
      <Modal.Root isOpened={isModalOpened} onOpenChange={modalHandler.set}>
        <Modal.Body>
          <Stack gap="md">
            {previewSection}
            {isPostLengthWarningVisible && (
              <Notice
                variant="warning"
                icon={<AlertTriangleIcon />}
                message={t("component.create-post-app-preview.warning")}
              />
            )}
          </Stack>
        </Modal.Body>
      </Modal.Root>
    </>
  );
}

const wrapText = (text: string, maxWidth: number) => {
  return text.split("\n").flatMap((line) => {
    const wrapped = [];
    for (let i = 0; i < line.length; i += maxWidth) {
      wrapped.push(line.slice(i, i + maxWidth));
    }

    return wrapped;
  });
};
